import React, { useEffect, useState, useRef } from "react";
import "../css/ImageScrollGallery.css";
import { Button, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faImage } from "@fortawesome/free-solid-svg-icons";
import {formatJournalPath} from "../utils/formatJournalPath";
import { fetchJson } from "../utils/fetchJson";
const ImageScrollGallery = () => {
  const [screenSize, setScreenSize] = useState("md");
  const [isLoading, setIsLoading] = useState(true);
  const [journals, setJournals] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const containerRef = useRef(null);

  // 屏幕尺寸检测
  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const checkScreenSize = () => {
    const width = window.innerWidth;
    if (width < 576) setScreenSize("xs");
    else if (width < 768) setScreenSize("sm");
    else if (width < 992) setScreenSize("md");
    else if (width < 1200) setScreenSize("lg");
    else setScreenSize("xl");
  };

  // 每屏显示数量
  const visibleCount = screenSize === "xs" ? 2 : 5;

  // 获取期刊数据
  useEffect(() => {
    setIsLoading(true);
    fetchJson("/json/journals.json")
      .then((res) => res.json())
      .then((data) => {
        const list = data.journals;

        // ⚡ 克隆头尾 visibleCount 张
        const head = list.slice(0, visibleCount);
        const tail = list.slice(-visibleCount);
        const cloned = [...tail, ...list, ...head];

        setJournals(cloned);
        setCurrentIndex(visibleCount); // 从真实第一张开始
      })
      .finally(() => setIsLoading(false));
  }, [visibleCount]);

  // 上一张
  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  // 下一张
  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  // 动画结束后瞬移
  const handleTransitionEnd = () => {
    setIsTransitioning(false);

    if (currentIndex < visibleCount) {
      // 到头部克隆区 → 跳到真实尾部
      const realIndex = journals.length - visibleCount * 2;
      setCurrentIndex(realIndex);
      containerRef.current.style.transition = "none";
      containerRef.current.style.transform = `translateX(-${
        realIndex * (100 / visibleCount)
      }%)`;
      requestAnimationFrame(() => {
        containerRef.current.style.transition = "transform 0.5s ease";
      });
    } else if (currentIndex >= journals.length - visibleCount) {
      // 到尾部克隆区 → 跳到真实头部
      setCurrentIndex(visibleCount);
      containerRef.current.style.transition = "none";
      containerRef.current.style.transform = `translateX(-${
        visibleCount * (100 / visibleCount)
      }%)`;
      requestAnimationFrame(() => {
        containerRef.current.style.transition = "transform 0.5s ease";
      });
    }
  };

  // 偏移计算
  const translateX = -(currentIndex * (100 / visibleCount));

  const renderJournalCard = (item, idx) => (
    <a
      key={`${item.id || "card"}-${idx}`}
      href={item.isComingSoon ? "#" : `journal/${formatJournalPath(item.title)}`}
      style={{
        textDecoration: "none",
        flex: `0 0 ${100 / visibleCount}%`,
        padding: "0 5px",
        boxSizing: "border-box",
      }}
    >
      <div className="article-card relative" style={{ paddingTop: "135%", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0 }}>
          {item.isComingSoon ? (
            <div className="d-flex justify-content-center align-items-center h-100 bg-dark text-white">
              {item.title}
            </div>
          ) : item.img ? (
            <img
              src={item.img}
              alt={item.title || item.id}
              className="w-100 h-100"
              style={{ objectFit: "cover", borderRadius: "4px" }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 border border-dashed">
              <FontAwesomeIcon icon={faImage} size="2x" />
            </div>
          )}
        </div>
      </div>
    </a>
  );

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center min-vh-50">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 text-muted">Loading journals, please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ overflow: "hidden" }}>
      <div
        className="d-flex"
        ref={containerRef}
        style={{
          transform: `translateX(${translateX}%)`,
          transition: isTransitioning ? "transform 0.5s ease" : "none",
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {journals.map((item, i) => renderJournalCard(item, i))}
      </div>

      <Stack direction="horizontal" gap={3} className="justify-center mt-3 mb-4">
        <Button onClick={handlePrev}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <div className="vr" />
        <Button onClick={handleNext}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </Stack>
    </div>
  );
};

export default ImageScrollGallery;
